import * as Yup from 'yup'

import { RIGHT_TYPE, RIGHT_TYPE_ONSITE } from '../../fields'
import {
  INVENTORY_TYPE,
  INVENTORY_TYPE_AD_PLACEMENTS,
  INVENTORY_TYPE_AD_UNITS,
  AD_PLACEMENTS,
  AD_UNITS,
  ALLOCATION_TYPE,
  IS_SUB_ALLOCATION,
  allocationGroups,
  BILLING_EVENT,
  rightTypes
} from './sectionsLists/OnsiteRightCreateSections/fields'
import {
  ALLOCATION,
  CPM,
  CPC,
  VIEWABLE_CPM
} from './sectionsLists/OnsiteRightCreateSections/GamOnsiteRightCreateSections/InventoryAccessAndAllocationSection/SubAllocationTables/fields'

import { getBillingEventType } from './sectionsLists/OnsiteRightCreateSections/GamOnsiteRightCreateSections/helpers'

// Helper function to validate price fields based on allocation type
const validatePriceField = (value, context) => {
  // Get the form values from context
  const formValues = context.from[1].value

  const billingEventType = getBillingEventType(formValues[ALLOCATION_TYPE], formValues[BILLING_EVENT])

  // Check if the current field is the one that needs validation based on allocation type
  const isCurrentFieldRequired =
    (billingEventType === CPM && context.path.endsWith('.' + CPM)) ||
    (billingEventType === CPC && context.path.endsWith('.' + CPC)) ||
    (billingEventType === VIEWABLE_CPM && context.path.endsWith('.' + VIEWABLE_CPM))

  if (isCurrentFieldRequired && !value) {
    return false
  }

  return true
}

export const gamAccountFieldsValidation = {
  [ALLOCATION]: Yup.mixed().when([RIGHT_TYPE, IS_SUB_ALLOCATION], {
    is: (rightType, isSubAllocation) => rightType === RIGHT_TYPE_ONSITE && !isSubAllocation,
    then: () =>
      Yup.mixed().test({
        name: 'validate-single-allocation-group',
        message: 'Invalid allocation value',
        test: function (_, context) {
          const formValues = context.options.context
          const allocationType = formValues[ALLOCATION_TYPE]

          const allocationGroup = allocationGroups[allocationType]

          // Get allocation value from current group
          const allocationValue = formValues?.[allocationGroup]?.[ALLOCATION]

          // Check if allocation is required and present
          if (!allocationValue) {
            return this.createError({
              message: 'Allocation is required',
              path: `${allocationGroup}.${ALLOCATION}`
            })
          }

          // Validate based on allocation group
          if (allocationGroup === rightTypes.percentage) {
            if (allocationValue < 0 || allocationValue > 100) {
              return this.createError({
                message: 'Percentage allocation must be between 0 and 100',
                path: `${allocationGroup}.${ALLOCATION}`
              })
            }
          }

          return true
        }
      })
  }),
  [CPM]: Yup.number().when([RIGHT_TYPE, IS_SUB_ALLOCATION, ALLOCATION_TYPE, BILLING_EVENT], {
    is: (rightType, isSubAllocation, allocationType, billingEvent) => {
      const billingEventType = getBillingEventType(allocationType, billingEvent)

      return rightType === RIGHT_TYPE_ONSITE && !isSubAllocation && billingEventType === CPM
    },
    then: () => Yup.number().required('CPM is required')
  }),
  [CPC]: Yup.number().when([RIGHT_TYPE, IS_SUB_ALLOCATION, ALLOCATION_TYPE, BILLING_EVENT], {
    is: (rightType, isSubAllocation, allocationType, billingEvent) => {
      const billingEventType = getBillingEventType(allocationType, billingEvent)

      return rightType === RIGHT_TYPE_ONSITE && !isSubAllocation && billingEventType === CPC
    },
    then: () => Yup.number().required('CPC is required')
  }),
  [VIEWABLE_CPM]: Yup.number().when([RIGHT_TYPE, IS_SUB_ALLOCATION, ALLOCATION_TYPE, BILLING_EVENT], {
    is: (rightType, isSubAllocation, allocationType, billingEvent) => {
      const billingEventType = getBillingEventType(allocationType, billingEvent)

      return rightType === RIGHT_TYPE_ONSITE && !isSubAllocation && billingEventType === VIEWABLE_CPM
    },
    then: () => Yup.number().required('Viewable CPM is required')
  }),
  [AD_PLACEMENTS]: Yup.array().when([RIGHT_TYPE, INVENTORY_TYPE], {
    is: (rightType, inventoryType) => rightType === RIGHT_TYPE_ONSITE && inventoryType === INVENTORY_TYPE_AD_PLACEMENTS,
    then: () =>
      Yup.array()
        .min(1, 'At least one placement is required')
        .when(IS_SUB_ALLOCATION, {
          is: isSubAllocation => isSubAllocation,
          then: () =>
            Yup.array()
              .min(1, 'At least one placement is required')
              .of(
                Yup.object()
                  .test({
                    name: 'validate-allocation-group',
                    message: 'Invalid allocation value',
                    test: function (value, context) {
                      const formValues = context.options.context
                      const allocationType = formValues[ALLOCATION_TYPE]

                      const allocationGroup = allocationGroups[allocationType]

                      // Get allocation value from current group
                      const allocationValue = value?.[allocationGroup]?.[ALLOCATION]

                      // Check if allocation is required and present
                      if (!allocationValue) {
                        return this.createError({
                          message: 'Allocation is required',
                          path: `${this.path}.${allocationGroup}.${ALLOCATION}`
                        })
                      }

                      // Validate based on allocation group
                      if (allocationGroup === rightTypes.percentage) {
                        if (allocationValue < 0 || allocationValue > 100) {
                          return this.createError({
                            message: 'Percentage allocation must be between 0 and 100',
                            path: `${this.path}.${allocationGroup}.${ALLOCATION}`
                          })
                        }
                      }

                      return true
                    }
                  })
                  .shape({
                    [CPM]: Yup.mixed().test({
                      name: 'cpm-required',
                      message: 'CPM is required',
                      test: validatePriceField
                    }),
                    [CPC]: Yup.mixed().test({
                      name: 'cpc-required',
                      message: 'CPC is required',
                      test: validatePriceField
                    }),
                    [VIEWABLE_CPM]: Yup.mixed().test({
                      name: 'viewable-cpm-required',
                      message: 'Viewable CPM is required',
                      test: validatePriceField
                    })
                  })
              )
        })
  }),

  [AD_UNITS]: Yup.array().when([RIGHT_TYPE, INVENTORY_TYPE], {
    is: (rightType, inventoryType) => rightType === RIGHT_TYPE_ONSITE && inventoryType === INVENTORY_TYPE_AD_UNITS,
    then: () =>
      Yup.array()
        .min(1, 'At least one ad unit is required')
        .when(IS_SUB_ALLOCATION, {
          is: isSubAllocation => isSubAllocation,
          then: () =>
            Yup.array()
              .min(1, 'At least one ad unit is required')
              .of(
                Yup.object()
                  .test({
                    name: 'validate-ad-unit-allocation-group',
                    message: 'Invalid allocation value',
                    test: function (value, context) {
                      const formValues = context.options.context
                      const allocationType = formValues[ALLOCATION_TYPE]
                      const adUnits = formValues[AD_UNITS] || []

                      // Check if this ad unit has a parent that's also in the list (skip validation if so)
                      const { parent } = value || {}
                      if (parent && Array.isArray(adUnits) && adUnits.some(u => u.id === parent)) {
                        return true
                      }

                      const allocationGroup = allocationGroups[allocationType]

                      // Get allocation value from current group
                      const allocationValue = value?.[allocationGroup]?.[ALLOCATION]

                      // Check if allocation is required and present
                      if (!allocationValue && allocationValue !== 0) {
                        return this.createError({
                          message: 'Allocation is required for ad units without a parent in the list',
                          path: `${this.path}.${allocationGroup}.${ALLOCATION}`
                        })
                      }

                      // Validate based on allocation group
                      if (allocationGroup === rightTypes.percentage) {
                        if (allocationValue < 0 || allocationValue > 100) {
                          return this.createError({
                            message: 'Percentage allocation must be between 0 and 100',
                            path: `${this.path}.${allocationGroup}.${ALLOCATION}`
                          })
                        }
                      }

                      return true
                    }
                  })
                  .shape({
                    [CPM]: Yup.mixed().test({
                      name: 'cpm-validate-only-if-no-parent-present',
                      message: 'CPM is required',
                      test: (value, context) => {
                        const parent = context.parent.parent
                        const adUnits = context.options.context[AD_UNITS] || []
                        // If there is a parent and it's present in adUnits, skip validation for this child
                        if (parent && Array.isArray(adUnits) && adUnits.some(u => u.id === parent)) {
                          return true
                        }

                        return validatePriceField(value, context)
                      }
                    }),

                    [CPC]: Yup.mixed().test({
                      name: 'cpc-validate-only-if-no-parent-present',
                      message: 'CPC is required',
                      test: (value, context) => {
                        const parent = context.parent.parent
                        const adUnits = context.options.context[AD_UNITS] || []
                        // If there is a parent and it's present in adUnits, skip validation for this child
                        if (parent && Array.isArray(adUnits) && adUnits.some(u => u.id === parent)) {
                          return true
                        }

                        return validatePriceField(value, context)
                      }
                    }),

                    [VIEWABLE_CPM]: Yup.mixed().test({
                      name: 'viewable-cpm-validate-only-if-no-parent-present',
                      message: 'Viewable CPM is required',
                      test: (value, context) => {
                        const parent = context.parent.parent
                        const adUnits = context.options.context[AD_UNITS] || []
                        // If there is a parent and it's present in adUnits, skip validation for this child
                        if (parent && Array.isArray(adUnits) && adUnits.some(u => u.id === parent)) {
                          return true
                        }

                        return validatePriceField(value, context)
                      }
                    })
                  })
              )
        })
  })
}
